import { createApi } from '@reduxjs/toolkit/query/react';
import { endpoints } from 'config/api';
import { axiosBaseQuery } from 'config/axiosBaseQuery';
import type { Role, CreateRoleDto, UpdateRoleDto, PermissionCategory } from './types';

export const rolesApi = createApi({
  reducerPath: 'rolesApi',
  baseQuery: axiosBaseQuery(),
  tagTypes: ['Roles'],
  endpoints: (builder) => ({
    /**
     * Get all roles accessible to the current user.
     * Optionally filter by companyId for company-specific views.
     */
    getRoles: builder.query<Role[], string | void>({
      query: (companyId) => ({
        url: companyId ? `${endpoints.roles.base}?companyId=${companyId}` : endpoints.roles.base,
        method: 'GET',
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ _id }) => ({ type: 'Roles' as const, id: _id })),
              { type: 'Roles', id: 'LIST' },
            ]
          : [{ type: 'Roles', id: 'LIST' }],
    }),

    /**
     * Get all available permissions with categories and metadata.
     */
    getPermissions: builder.query<PermissionCategory[], void>({
      query: () => ({
        url: endpoints.roles.permissions,
        method: 'GET',
      }),
    }),

    /**
     * Get a specific role by ID.
     */
    getRoleById: builder.query<Role, string>({
      query: (id) => ({
        url: endpoints.roles.byId(id),
        method: 'GET',
      }),
      providesTags: (result, error, id) => [{ type: 'Roles', id }],
    }),

    /**
     * Create a new custom role.
     */
    createRole: builder.mutation<Role, CreateRoleDto>({
      query: (data) => ({
        url: endpoints.roles.base,
        method: 'POST',
        data,
      }),
      // Invalidate all roles queries to trigger refetch
      invalidatesTags: ['Roles'],
    }),

    /**
     * Update an existing role.
     */
    updateRole: builder.mutation<Role, { id: string; data: UpdateRoleDto }>({
      query: ({ id, data }) => ({
        url: endpoints.roles.byId(id),
        method: 'PUT',
        data,
      }),
      // Invalidate all roles queries to trigger refetch
      invalidatesTags: ['Roles'],
    }),

    /**
     * Delete a custom role.
     */
    deleteRole: builder.mutation<void, string>({
      query: (id) => ({
        url: endpoints.roles.byId(id),
        method: 'DELETE',
      }),
      // Invalidate all roles queries to trigger refetch
      invalidatesTags: ['Roles'],
    }),

    /**
     * Seed system roles for a company (SUPERADMIN only).
     */
    seedSystemRoles: builder.mutation<Role[], string>({
      query: (companyId) => ({
        url: endpoints.roles.seed(companyId),
        method: 'POST',
      }),
      // Invalidate all roles queries to trigger refetch
      invalidatesTags: ['Roles'],
    }),
  }),
});

export const {
  useGetRolesQuery,
  useGetPermissionsQuery,
  useGetRoleByIdQuery,
  useCreateRoleMutation,
  useUpdateRoleMutation,
  useDeleteRoleMutation,
  useSeedSystemRolesMutation,
  useLazyGetRolesQuery,
  useLazyGetRoleByIdQuery,
} = rolesApi;
